import { createBrowserRouter, redirect } from "react-router-dom";

import { ROOM_TYPE_PREFIX, roomPath, roomTypeFromSegment } from "@/deal-room/room-path";
import type { Room } from "@/deal-room/schema";
import { roomSchema } from "@/deal-room/schema";

import { Home } from "./Home";
import { LegacyRoomRedirect } from "./LegacyRoomRedirect";
import { NotFound } from "./NotFound";
import { PreviewRoomShell } from "./PreviewRoom";
import { RoomShell } from "./Room";
import { RoomFirstPage, RoomPage } from "./RoomPages";
import { RootLayout } from "./RootLayout";
import { SignIn } from "./SignIn";

// Loader: fetch a room from /api/rooms/:slug. Used by both the production
// (gated) and preview (open) routes — the API itself is the same.
//
// 401 on the production path → redirect to /sign-in.
// 404 → throw a 404 Response, which react-router maps to the route's
// errorElement.
async function loadRoom({
    typeSegment,
    slug,
    requireAuth,
    nextPath,
}: {
    typeSegment: string;
    slug: string;
    requireAuth: boolean;
    nextPath: string;
}): Promise<Room> {
    // The first URL segment names the room type (room-path.ts). An unknown
    // segment is a 404 before any fetch; the legacy "rooms" segment never
    // reaches here (LegacyRoomRedirect owns it).
    const type = roomTypeFromSegment(typeSegment);
    if (!type || typeSegment === "rooms") throw new Response("Not found", { status: 404 });
    const endpoint = requireAuth ? `/api/rooms/${slug}` : `/api/rooms/preview/${slug}`;
    const res = await fetch(endpoint, { credentials: "include" });
    if (res.status === 401) {
        // Both gated (/api/rooms/<slug>) and preview (/api/rooms/preview/<slug>)
        // can 401 — preview requires any session, gated requires a recipient
        // session. Either way, send the user to /sign-in.
        const params = new URLSearchParams({ room: slug, next: nextPath });
        throw redirect(`/sign-in?${params.toString()}`);
    }
    if (res.status === 404) {
        throw new Response("Room not found", { status: 404 });
    }
    if (!res.ok) {
        throw new Response(`Storage error (${res.status})`, { status: 500 });
    }
    const raw = await res.json();
    const parsed = roomSchema.safeParse(raw);
    if (!parsed.success) {
        throw new Response("Stored room failed schema validation", { status: 500 });
    }
    // A room opened under another type's segment (a link built before the
    // type was known: the staff directory, a magic link's default `next`)
    // lands on its own segment with the page and query string kept.
    const room = parsed.data;
    if (room.roomType !== type) {
        const wrong = roomPath(slug, !requireAuth, type);
        const right = roomPath(slug, !requireAuth, room.roomType);
        const at = nextPath.startsWith(wrong) ? nextPath.slice(wrong.length) : "";
        throw redirect(right + at);
    }
    return room;
}

// shouldRevalidate: skip the room loader when only `:page` changes inside
// the same room. This is the key to keeping the header in place without a
// flicker — the parent's data stays cached, only the leaf re-renders. A
// revalidation on the same URL (useRevalidator, after staff edit the room's
// rates) still refetches.
const stableUnlessSlugChanges = ({
    currentUrl,
    nextUrl,
    currentParams,
    nextParams,
}: {
    currentUrl: URL;
    nextUrl: URL;
    currentParams: Record<string, string | undefined>;
    nextParams: Record<string, string | undefined>;
}): boolean => currentParams.slug !== nextParams.slug || currentUrl.href === nextUrl.href;

export const router = createBrowserRouter([
    {
        path: "/",
        element: <RootLayout />,
        errorElement: <NotFound />,
        children: [
            { index: true, element: <Home /> },
            { path: "sign-in", element: <SignIn /> },

            // One gated + one preview route per room type (deal-room,
            // content-optimization, pitch). Explicit prefixes, not a `:type` param:
            // a dynamic first segment outranks the `rooms/*` legacy splat in
            // react-router's route scoring and swallowed old links.
            //
            // Production room — gated. The shell loads the room JSON once and
            // hosts RoomLayout (sticky header). Page children read room data
            // from the matched "room:<type>" loader (RoomPages useRoom) so
            // changing pages doesn't refetch.
            ...Object.values(ROOM_TYPE_PREFIX).flatMap((prefix) => [
                {
                    id: `room:${prefix.slice(1)}`,
                    path: `${prefix.slice(1)}/:slug`,
                    loader: ({
                        params,
                        request,
                    }: {
                        params: Record<string, string | undefined>;
                        request: Request;
                    }) => {
                        const url = new URL(request.url);
                        return loadRoom({
                            typeSegment: prefix.slice(1),
                            slug: (params.slug ?? "").toLowerCase(),
                            requireAuth: true,
                            nextPath: url.pathname + url.search,
                        });
                    },
                    shouldRevalidate: stableUnlessSlugChanges,
                    element: <RoomShell />,
                    children: [
                        { index: true, element: <RoomFirstPage /> },
                        { path: ":page", element: <RoomPage /> },
                    ],
                },
                // Preview room — open. Same shape, different shell + loader.
                {
                    id: `preview-room:${prefix.slice(1)}`,
                    path: `${prefix.slice(1)}/preview/:slug`,
                    loader: ({
                        params,
                        request,
                    }: {
                        params: Record<string, string | undefined>;
                        request: Request;
                    }) => {
                        const url = new URL(request.url);
                        return loadRoom({
                            typeSegment: prefix.slice(1),
                            slug: (params.slug ?? "").toLowerCase(),
                            requireAuth: false,
                            nextPath: url.pathname + url.search,
                        });
                    },
                    shouldRevalidate: stableUnlessSlugChanges,
                    element: <PreviewRoomShell />,
                    children: [
                        { index: true, element: <RoomFirstPage /> },
                        { path: ":page", element: <RoomPage /> },
                    ],
                },
            ]),

            { path: "rooms/*", element: <LegacyRoomRedirect /> },

            { path: "*", element: <NotFound /> },
        ],
    },
]);
